/**
 * Google Analytics 4 Utility Module
 * 
 * Provides centralized tracking for page views, custom events, and errors.
 * All GA calls are no-ops if GA_TRACKING_ID is not configured.
 */

export const GA_TRACKING_ID = process.env.REACT_APP_GA_TRACKING_ID
const IS_DEV = process.env.NODE_ENV === 'development'

/**
 * Check if GA is properly configured
 */
export const isGAEnabled = () => {
    return !!GA_TRACKING_ID && typeof window !== 'undefined' && typeof window.gtag === 'function'
}

/**
 * Log analytics events to console in development
 */
const logDevEvent = (eventName, params) => {
    if (IS_DEV) {
        console.groupCollapsed(`📊 Analytics: ${eventName}`)
        console.log('Params:', params)
        console.log('GA Enabled:', isGAEnabled())
        console.groupEnd()
    }
}

/**
 * Track a page view
 * @param {string} url - The URL/path to track
 * @param {string} [title] - Optional page title
 */
export const pageview = (url, title) => {
    logDevEvent('page_view', { url, title })

    if (!isGAEnabled()) return

    window.gtag('config', GA_TRACKING_ID, {
        page_path: url,
        page_title: title
    })
}

/**
 * Track a custom event
 * @param {Object} params - Event parameters
 * @param {string} params.action - Event action name (e.g., 'poll_created')
 * @param {string} [params.category] - Event category (default: 'engagement')
 * @param {string} [params.label] - Event label for additional context
 * @param {number} [params.value] - Numeric value associated with the event
 * @param {Object} [params.additionalParams] - Any additional custom parameters
 */
export const event = ({ action, category = 'engagement', label, value, ...additionalParams }) => {
    logDevEvent(action, { category, label, value, ...additionalParams })

    if (!isGAEnabled()) return

    window.gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value,
        ...additionalParams
    })
}

/**
 * Track an error event
 * @param {string} errorType - Type of error (e.g., 'error_vote', 'error_poll_load')
 * @param {Object} params - Error details
 */
export const trackError = (errorType, params = {}) => {
    logDevEvent(errorType, { category: 'error', ...params })

    if (!isGAEnabled()) return

    window.gtag('event', errorType, {
        event_category: 'error',
        ...params
    })
}

// ============================================
// Convenience methods for common events
// ============================================

// HomePage Events
export const trackPollCreated = (pollId, titleLength, source = 'manual') => {
    event({ action: 'poll_created', poll_id: pollId, title_length: titleLength, poll_source: source })
}

export const trackMenuImageUploaded = (fileSize, fileType) => {
    event({ action: 'menu_image_uploaded', file_size: fileSize, file_type: fileType })
}

export const trackPollJoined = (pollCode) => {
    event({ action: 'poll_joined', poll_code: pollCode })
}

// PollPage Events
export const trackPollViewed = (pollId, choiceCount) => {
    event({ action: 'poll_viewed', poll_id: pollId, choice_count: choiceCount })
}

export const trackVoteCast = (pollId, choiceId, voteType) => {
    event({ action: 'vote_cast', poll_id: pollId, choice_id: choiceId, vote_type: voteType })
}

export const trackVoteRemoved = (pollId, choiceId) => {
    event({ action: 'vote_removed', poll_id: pollId, choice_id: choiceId })
}

export const trackPollLinkCopied = (pollId) => {
    event({ action: 'poll_link_copied', poll_id: pollId })
}

export const trackChoiceAdded = (pollId, textLength) => {
    event({ action: 'choice_added', poll_id: pollId, text_length: textLength })
}

export const trackEditModeToggled = (pollId, enabled) => {
    event({ action: 'edit_mode_toggled', poll_id: pollId, enabled: enabled })
}

export const trackPollTitleEdited = (pollId) => {
    event({ action: 'poll_title_edited', poll_id: pollId })
}

export const trackChoiceTextEdited = (pollId, choiceId) => {
    event({ action: 'choice_text_edited', poll_id: pollId, choice_id: choiceId })
}

export const trackChoiceDeleted = (pollId, choiceId) => {
    event({ action: 'choice_deleted', poll_id: pollId, choice_id: choiceId })
}

export const trackCurrencyChanged = (pollId, currencyCode) => {
    event({ action: 'currency_changed', poll_id: pollId, currency_code: currencyCode })
}

export const trackCloneModeToggled = (pollId, enabled) => {
    event({ action: 'clone_mode_toggled', poll_id: pollId, enabled: enabled })
}

export const trackClonePollCreated = (sourcePollId, choiceCount) => {
    event({ action: 'clone_poll_created', source_poll_id: sourcePollId, choice_count: choiceCount })
}

export const trackRealtimeReconnected = (pollId) => {
    event({ action: 'realtime_reconnected', poll_id: pollId })
}

// MenuPollReviewPage Events
export const trackReviewPageViewed = (pollId, choiceCount) => {
    event({ action: 'review_page_viewed', poll_id: pollId, choice_count: choiceCount })
}

export const trackReviewTitleEdited = (pollId) => {
    event({ action: 'review_title_edited', poll_id: pollId })
}

export const trackReviewChoiceEdited = (pollId, choiceId) => {
    event({ action: 'review_choice_edited', poll_id: pollId, choice_id: choiceId })
}

export const trackReviewChoiceDeleted = (pollId, choiceId) => {
    event({ action: 'review_choice_deleted', poll_id: pollId, choice_id: choiceId })
}

export const trackReviewChoiceAdded = (pollId) => {
    event({ action: 'review_choice_added', poll_id: pollId })
}

export const trackReviewPriceChanged = (pollId, choiceId) => {
    event({ action: 'review_price_changed', poll_id: pollId, choice_id: choiceId })
}

export const trackReviewOptionsChanged = (pollId, choiceId, optionCount) => {
    event({ action: 'review_options_changed', poll_id: pollId, choice_id: choiceId, option_count: optionCount })
}

export const trackReviewCurrencySelected = (pollId, currencyCode) => {
    event({ action: 'review_currency_selected', poll_id: pollId, currency_code: currencyCode })
}

export const trackPollConfirmed = (pollId, choiceCount, hasPrices) => {
    event({ action: 'poll_confirmed', poll_id: pollId, choice_count: choiceCount, has_prices: hasPrices })
}

export const trackBackToHomeClicked = (pollId) => {
    event({ action: 'back_to_home_clicked', poll_id: pollId })
}

// AdminPage Events
export const trackAdminLoginSuccess = () => {
    event({ action: 'admin_login_success', category: 'admin' })
}

export const trackAdminLoginFailed = (errorType) => {
    event({ action: 'admin_login_failed', category: 'admin', error_type: errorType })
}

export const trackAdminLogout = () => {
    event({ action: 'admin_logout', category: 'admin' })
}

export const trackLlmProviderChanged = (providerName) => {
    event({ action: 'llm_provider_changed', category: 'admin', provider_name: providerName })
}

export const trackLlmModelChanged = (provider, modelName) => {
    event({ action: 'llm_model_changed', category: 'admin', provider: provider, model_name: modelName })
}

export const trackLlmModelAdded = (provider, modelName) => {
    event({ action: 'llm_model_added', category: 'admin', provider: provider, model_name: modelName })
}

export const trackLlmModelDeleted = (provider, modelName) => {
    event({ action: 'llm_model_deleted', category: 'admin', provider: provider, model_name: modelName })
}

export const trackVideoUploaded = (fileCount, totalSize) => {
    event({ action: 'video_uploaded', category: 'admin', file_count: fileCount, total_size: totalSize })
}

export const trackVideoSelected = (videoFilename) => {
    event({ action: 'video_selected', category: 'admin', video_filename: videoFilename })
}

export const trackVideoDeleted = (videoFilename) => {
    event({ action: 'video_deleted', category: 'admin', video_filename: videoFilename })
}

export const trackAllVideosDeleted = (count) => {
    event({ action: 'all_videos_deleted', category: 'admin', count: count })
}

export const trackDatabaseCleanup = () => {
    event({ action: 'database_cleanup', category: 'admin' })
}

// Error Events
export const trackErrorPollLoad = (pollId, errorMessage) => {
    trackError('error_poll_load', { poll_id: pollId, error_message: errorMessage })
}

export const trackErrorPollCreate = (errorMessage) => {
    trackError('error_poll_create', { error_message: errorMessage })
}

export const trackErrorMenuUpload = (errorMessage, fileSize) => {
    trackError('error_menu_upload', { error_message: errorMessage, file_size: fileSize })
}

export const trackErrorVote = (pollId, choiceId, errorMessage) => {
    trackError('error_vote', { poll_id: pollId, choice_id: choiceId, error_message: errorMessage })
}

export const trackErrorVoteRemove = (pollId, choiceId, errorMessage) => {
    trackError('error_vote_remove', { poll_id: pollId, choice_id: choiceId, error_message: errorMessage })
}

export const trackErrorChoiceAdd = (pollId, errorMessage) => {
    trackError('error_choice_add', { poll_id: pollId, error_message: errorMessage })
}

export const trackErrorChoiceEdit = (pollId, choiceId, errorMessage) => {
    trackError('error_choice_edit', { poll_id: pollId, choice_id: choiceId, error_message: errorMessage })
}

export const trackErrorChoiceDelete = (pollId, choiceId, errorMessage) => {
    trackError('error_choice_delete', { poll_id: pollId, choice_id: choiceId, error_message: errorMessage })
}

export const trackErrorTitleEdit = (pollId, errorMessage) => {
    trackError('error_title_edit', { poll_id: pollId, error_message: errorMessage })
}

export const trackErrorCurrencySave = (pollId, errorMessage) => {
    trackError('error_currency_save', { poll_id: pollId, error_message: errorMessage })
}

export const trackErrorClonePoll = (sourcePollId, errorMessage) => {
    trackError('error_clone_poll', { source_poll_id: sourcePollId, error_message: errorMessage })
}

export const trackErrorPollConfirm = (pollId, errorMessage) => {
    trackError('error_poll_confirm', { poll_id: pollId, error_message: errorMessage })
}

export const trackErrorAdminLogin = (errorType) => {
    trackError('error_admin_login', { error_type: errorType })
}

export const trackErrorVideoUpload = (filename, errorMessage) => {
    trackError('error_video_upload', { filename: filename, error_message: errorMessage })
}

export const trackErrorLlmConfig = (provider, errorMessage) => {
    trackError('error_llm_config', { provider: provider, error_message: errorMessage })
}

/**
 * Send Web Vitals metrics to GA
 * @param {Object} metric - Web Vitals metric object
 */
export const sendWebVitalsToGA = ({ name, delta, id }) => {
    logDevEvent('web_vitals', { name, delta, id })

    if (!isGAEnabled()) return

    window.gtag('event', name, {
        event_category: 'web_vitals',
        event_label: id,
        value: Math.round(name === 'CLS' ? delta * 1000 : delta),
        non_interaction: true
    })
}
